import { action, makeObservable, observable } from 'mobx';
import { BetBlock, RowBetBlock } from '../game/buttons';
import { roundsInfoStore } from './index';
import { betTypes } from '../enums/index';
import { BetStatus, betStore, freeBetStore } from '@pascal/pascal-core';
import { gameService } from '../services';
import { socketEventsHandlerDelays } from '../enums/replaySocetEventsDelays';

class DoBetsStore {
  betAmount = 0;
  betBlocks = [];
  rowBetInfo = {};
  rowBetBlocks = [];
  betBlocksData = {};
  hoveredBlock = null;
  isBetsClosed = false;
  isShowBetBlocks = false;
  isRequiredRedrawGame = true;
  selectedBetHistoryItem = {};
  isBetBlocksClickable = false;
  mouseOnCanvas = { x: 0, y: 0 };

  constructor() {
    makeObservable(this, {
      betBlocks: observable,
      betAmount: observable,
      isBetsClosed: observable,
      rowBetBlocks: observable,
      hoveredBlock: observable,
      betBlocksData: observable,
      mouseOnCanvas: observable,
      isShowBetBlocks: observable,
      isBetBlocksClickable: observable,
      isRequiredRedrawGame: observable,
      selectedBetHistoryItem: observable,
      setBetAmount: action.bound,
      setBetBlocks: action.bound,
      getDoBetState: action.bound,
      hoveringBlock: action.bound,
      resetBetBlocks: action.bound,
      setHoveredBlock: action.bound,
      setBetBlocksData: action.bound,
      setMouseOnCanvas: action.bound,
      setBetClosedState: action.bound,
      setIsShowBetBlocks: action.bound,
      handleClickOnCanvas: action.bound,
      setBetBlocksClicked: action.bound,
      setIsRequiredRedrawGame: action.bound,
      setIsBetBlocksClickable: action.bound,
      handleReplayGame: action.bound,
      setSelectedBetHistoryItem: action.bound,
    });
  }

  setIsRequiredRedrawGame(bool) {
    this.isRequiredRedrawGame = bool;
  }

  setMouseOnCanvas(x, y) {
    this.mouseOnCanvas = { x, y };
  }

  setIsShowBetBlocks(bool) {
    this.isShowBetBlocks = bool;
  }

  setIsBetBlocksClickable(bool) {
    this.isBetBlocksClickable = bool;
  }

  setBetAmount(amount) {
    this.betAmount = amount;
    this.setIsRequiredRedrawGame(true);
  }

  setBetClosedState(state) {
    this.isBetsClosed = state;
  }

  setBetBlocks(betBlocksData) {
    this.setBetBlocksData(betBlocksData);
    this.betBlocks = [];
    this.rowBetInfo = {};
    this.rowBetBlocks = [];
    const { betsHistory, lastResult } = roundsInfoStore;
    const { activeFreeBet } = freeBetStore;
    const isHaveActiveFreeBet = Object.values(activeFreeBet).length;
    const alreadyBusyPoints = {};
    let x = 0;

    for (const roundId in betsHistory) {
      betsHistory[roundId].forEach((bet) => {
        if (alreadyBusyPoints[bet.point]) {
          alreadyBusyPoints[bet.point] = [...alreadyBusyPoints[bet.point], roundId];
        } else {
          alreadyBusyPoints[bet.point] = [roundId];
        }
      });
    }

    for (const roundId in betBlocksData) {
      x += 100;
      betBlocksData[roundId].forEach((betBlockData) => {
        const point = betBlockData.min;
        const isDisabled = isHaveActiveFreeBet && betBlockData.odd < activeFreeBet.minOdd;
        if (alreadyBusyPoints[point]) {
          if (!alreadyBusyPoints[point].includes(roundId)) {
            this.betBlocks.push(BetBlock(betTypes.range, roundId, x, point, betBlockData.odd, 100, 50, isDisabled));
          }
        } else {
          this.betBlocks.push(BetBlock(betTypes.range, roundId, x, point, betBlockData.odd, 100, 50, isDisabled));
        }
      });
    }

    const underOverBetIsDisabled = isHaveActiveFreeBet && 2 < activeFreeBet.minOdd;
    this.betBlocks.push(
      BetBlock(betTypes.over, this.betBlocks[0].roundId, -165, lastResult + 12, 2, 150, 75, underOverBetIsDisabled)
    );
    this.betBlocks.push(
      BetBlock(betTypes.under, this.betBlocks[0].roundId, -165, lastResult - 5, 2, 150, 75, underOverBetIsDisabled)
    );
    this.betBlocks.forEach((betBlock, index) => {
      if (betBlock.type === betTypes.range) {
        if (this.rowBetInfo[betBlock.point]) {
          this.rowBetInfo[betBlock.point].push(index);
        } else {
          this.rowBetInfo[betBlock.point] = [index];
        }
      }
    });

    for (const point in this.rowBetInfo) {
      this.rowBetBlocks.push(RowBetBlock(point, this.rowBetInfo[point]));
    }
  }

  handleClickOnCanvas(x, y) {
    if (this.isBetBlocksClickable) {
      //check click on betBlocks
      this.betBlocks.forEach((betBlock) => {
        if (betBlock.isInRange(x, y)) {
          betBlock.onclick();
        }
      });

      //check click on rowBetBlocks
      this.rowBetBlocks.forEach((rowBlock) => {
        if (rowBlock.isInRange(x, y)) {
          rowBlock.onclick();
        }
      });
    }
  }

  getDoBetState() {
    const clickedBlocks = this.betBlocks.filter((block) => block.clicked);

    return clickedBlocks.map((block) => ({
      roundId: block.roundId,
      amount: block.amount,
      point: block.type === betTypes.range ? block.point : roundsInfoStore.presentPoint.point,
      marketType: block.type,
    }));
  }

  setBetBlocksData(betBlocksData) {
    this.betBlocksData = betBlocksData;
  }

  resetBetBlocks() {
    this.setBetBlocks(this.betBlocksData);
  }

  setBetBlocksClicked(bool) {
    this.betBlocks.forEach((block) => {
      block.clicked = bool;
    });
  }

  setSelectedBetHistoryItem(data) {
    this.selectedBetHistoryItem = data;
  }

  setHoveredBlock(data) {
    this.hoveredBlock = data;
    this.setIsRequiredRedrawGame(true);
  }

  hoveringBlock() {
    let foundHoveredBlock = null;
    this.betBlocks.forEach((betBlockData) => {
      if (betBlockData.isInRange(this.mouseOnCanvas.x, this.mouseOnCanvas.y)) {
        foundHoveredBlock = betBlockData;
      }
    });

    this.rowBetBlocks.forEach((betBlockData) => {
      if (betBlockData.isInRange(this.mouseOnCanvas.x, this.mouseOnCanvas.y)) {
        foundHoveredBlock = betBlockData;
      }
    });

    if ((this.hoveredBlock === null) ^ (foundHoveredBlock === null)) {
      this.setHoveredBlock(foundHoveredBlock);
    } else if (this.hoveredBlock && foundHoveredBlock) {
      if (
        (this.hoveredBlock && foundHoveredBlock.x !== this.hoveredBlock.x) ||
        foundHoveredBlock.y !== this.hoveredBlock.y
      ) {
        this.setHoveredBlock(foundHoveredBlock);
      }
    }
  }

  handleReplayGame() {
    const { repliedBetData } = betStore;
    const roundInfo = {
      duration: 7,
      betsClosedDuration: 3.0,
      dots: repliedBetData.replayData.resultsAndDots.filter((el) => el.roundId !== repliedBetData.roundId),
      betBlocks: repliedBetData.replayData.betBlocks,
    };
    roundsInfoStore.setRoundsInfo(roundInfo);
    gameService.handleRoundStart({
      data: {
        additionalData: { betBlocks: repliedBetData.replayData.betBlocks },
        results: repliedBetData.replayData.results,
        startedRound: { roundId: repliedBetData.additionalParams.betCurrentRoundId },
      },
    });

    setTimeout(() => {
      gameService.handleBetsClosed();
    }, socketEventsHandlerDelays.betsClosedDelay);

    setTimeout(() => {
      gameService.handleDoBetResult({
        ...repliedBetData,
        responseCode: 0,
        betData: [
          {
            id: repliedBetData.id,
            roundId: repliedBetData.roundId,
            amount: repliedBetData.amount,
            coinAmount: repliedBetData.coinAmount,
            markets: repliedBetData.additionalParams.markets.map((el) => ({
              ...el,
              status: BetStatus.Pending,
              winAmount: null,
            })),
          },
        ],
      });
    }, socketEventsHandlerDelays.doBetResultDelay);

    setTimeout(() => {
      gameService.handleRoundFin({
        data: {
          finishedRound: {
            amount: repliedBetData.amount,
            id: repliedBetData.id,
            gameId: repliedBetData.gameId,
            roundId: repliedBetData.roundId,
            gameTypeId: repliedBetData.gameTypeId,
            status: repliedBetData.status,
            roundData: {
              betBlocks: repliedBetData.replayData.betBlocks,
              dots: repliedBetData.replayData.resultsAndDots.find((el) => el.roundId === repliedBetData.roundId).dots,
              resultPoint: repliedBetData.roundData.resultPoint,
              previousPoint: repliedBetData.roundData.previousPoint,
            },
          },
          bets: [repliedBetData],
        },
      });
    }, socketEventsHandlerDelays.roundFinDelay);
  }
}

export const doBetsStore = new DoBetsStore();
